import React, { useState, useRef, useEffect } from "react";
import { DateRange } from "react-date-range";
import { format, isToday, isTomorrow, startOfWeek, endOfWeek, addWeeks, startOfMonth, endOfMonth } from 'date-fns';
import { enUS, nl } from 'date-fns/locale';
import { useTranslation } from "react-i18next";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import ArrowDownIcon from "../assets/image svg/chevron down.svg";
import DatumIcon from "../assets/image svg/Calendar-N.svg";

const DateRangePicker = ({ onDateChange, initialDateFilter }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [displayText, setDisplayText] = useState(
    initialDateFilter || t("all_dates")
  );
  const datePickerRef = useRef(null);

  // Get the appropriate locale based on current language
  const getLocale = () => {
    const currentLanguage = i18n.language;
    return currentLanguage === 'nl' ? nl : enUS;
  };

  // Handle clicks outside the date picker to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target) &&
        !event.target.closest('.rdrCalendarWrapper') && // Don't close when clicking calendar elements
        !event.target.closest('.rdrDateDisplayWrapper') // Don't close when clicking date display
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Set initial date range based on filter text
  useEffect(() => {
    if (initialDateFilter) {
      const today = new Date();

      switch (initialDateFilter) {
        case t("today"):
          setDateRange([
            {
              startDate: today,
              endDate: today,
              key: "selection",
            },
          ]);
          break;
        case t("tomorrow"):
          const tomorrow = new Date();
          tomorrow.setDate(today.getDate() + 1);
          setDateRange([
            {
              startDate: tomorrow,
              endDate: tomorrow,
              key: "selection",
            },
          ]);
          break;
        case t("this_week"):
          setDateRange([
            {
              startDate: startOfWeek(today, { weekStartsOn: 1 }),
              endDate: endOfWeek(today, { weekStartsOn: 1 }),
              key: "selection",
            },
          ]);
          break;
        case t("next_week"):
          const nextWeekStart = startOfWeek(addWeeks(today, 1), {
            weekStartsOn: 1,
          });
          const nextWeekEnd = endOfWeek(addWeeks(today, 1), {
            weekStartsOn: 1,
          });
          setDateRange([
            {
              startDate: nextWeekStart,
              endDate: nextWeekEnd,
              key: "selection",
            },
          ]);
          break;
        case t("this_month"):
          setDateRange([
            {
              startDate: startOfMonth(today),
              endDate: endOfMonth(today),
              key: "selection",
            },
          ]);
          break;
        default:
          // All dates or unknown filter
          break;
      }
    }
  }, [initialDateFilter, t]);

  const handleSelect = (ranges) => {
    setDateRange([ranges.selection]);
  };

  const applyDateRange = () => {
    const { startDate, endDate } = dateRange[0];

    // Check if both dates are the same
    if (startDate.getTime() === endDate.getTime()) {
      if (isToday(startDate)) {
        setDisplayText(t("today"));
        onDateChange(t("today"), { startDate, endDate });
      } else if (isTomorrow(startDate)) {
        setDisplayText(t("tomorrow"));
        onDateChange(t("tomorrow"), { startDate, endDate });
      } else {
        setDisplayText(format(startDate, "d MMM"));
        onDateChange(format(startDate, "d MMM"), { startDate, endDate });
      }
    } else {
      // Check for special ranges
      const today = new Date();
      const thisWeekStart = startOfWeek(today, { weekStartsOn: 1 });
      const thisWeekEnd = endOfWeek(today, { weekStartsOn: 1 });
      const nextWeekStart = startOfWeek(addWeeks(today, 1), {
        weekStartsOn: 1,
      });
      const nextWeekEnd = endOfWeek(addWeeks(today, 1), { weekStartsOn: 1 });
      const thisMonthStart = startOfMonth(today);
      const thisMonthEnd = endOfMonth(today);

      if (
        startDate.getTime() === thisWeekStart.getTime() &&
        endDate.getTime() === thisWeekEnd.getTime()
      ) {
        setDisplayText(t("this_week"));
        onDateChange(t("this_week"), { startDate, endDate });
      } else if (
        startDate.getTime() === nextWeekStart.getTime() &&
        endDate.getTime() === nextWeekEnd.getTime()
      ) {
        setDisplayText(t("next_week"));
        onDateChange(t("next_week"), { startDate, endDate });
      } else if (
        startDate.getTime() === thisMonthStart.getTime() &&
        endDate.getTime() === thisMonthEnd.getTime()
      ) {
        setDisplayText(t("this_month"));
        onDateChange(t("this_month"), { startDate, endDate });
      } else {
        // Custom date range
        setDisplayText(
          `${format(startDate, "d MMM")} - ${format(endDate, "d MMM")}`
        );
        onDateChange(
          `${format(startDate, "d MMM")} - ${format(endDate, "d MMM")}`,
          { startDate, endDate }
        );
      }
    }

    setIsOpen(false);
  };

  const handleClearDates = () => {
    setDisplayText(t("all_dates"));
    onDateChange(t("all_dates"), null);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={datePickerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-center items-center bg-[#1D1726] px-3 py-2 !border-transparent !rounded-[10px] text-white btn btn-dropdown dropdown-toggle"
      >
        <img src={DatumIcon} alt="datum" className="mr-2" />
        <span className="mx-1 selectedOption">{displayText}</span>
        <img src={ArrowDownIcon} alt="arrow down" className="ml-2" />
      </button>

      {isOpen && (
        <div className="max-lg:top-[60px] max-lg:right-0 max-lg:bottom-auto max-lg:left-0 z-[999999] max-lg:fixed absolute flex flex-col justify-center items-center bg-[#1D1726] shadow-lg max-lg:mx-auto mt-2 p-4 border border-[#3A2D4C] rounded-[10px] max-lg:w-full max-lg:h-auto max-lg:overflow-auto">
          <DateRange
            editableDateInputs={true}
            onChange={handleSelect}
            moveRangeOnFirstSelection={false}
            ranges={dateRange}
            months={1}
            direction="vertical"
            className="max-lg:mx-auto rounded-[10px] !rounded-t-[10px] date-range-custom"
            rangeColors={["#AD7AEF"]}
            color="#AD7AEF"
            locale={getLocale()}
          />
          <div className="flex justify-between mt-4 w-full">
            <button
              onClick={handleClearDates}
              className="bg-[#3A2D4C] hover:bg-[#4A3D5C] px-4 py-2 rounded-md text-white transition-colors"
            >
              {t("clear")}
            </button>
            <button
              onClick={applyDateRange}
              className="bg-[#AD7AEF] hover:bg-[#9D6ADF] px-4 py-2 rounded-md text-white transition-colors"
            >
              {t("apply")}
            </button>
          </div>
        </div>
      )}

      {/* Add a backdrop when the date picker is open on mobile */}
      {isOpen && (
        <div
          className="hidden max-lg:block z-[999998] fixed inset-0 bg-black bg-opacity-50"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Custom styles for the date picker */}
      <div style={{
        /* Your styles here */
      }}>
      </div>
    </div>
  );
};

export default DateRangePicker;
